import {
  BufferAttribute,
  BufferGeometry,
  Color,
  Mesh,
  Points,
  Vector3,
  Camera,
  LineSegments,
  Object3D,
  Line,
  LineBasicMaterial,
} from "three";
import { LinearEncoding, NearestFilter, RGBAFormat } from "three/src/constants";
import { WebGLRenderer } from "three/src/renderers/WebGLRenderer";
import { WebGLRenderTarget } from "three/src/renderers/WebGLRenderTarget";
import { Scene } from "three/src/scenes/Scene";
import { RayCaster } from "./ray-caster";
import { Viewer } from "./main";
import { BasicPickerMaterial, CustomSpriteMaterial } from "./rendering";
import { allIndexOf, eventToPixel } from "./utilities";

/** Clone geometry and add gpu_index */
export const geometryWithIndex = (mesh) => {
  const pickerGeometry = mesh.geometry.clone();
  const indices = new Int32Array(pickerGeometry.attributes.position.count);
  pickerGeometry.setAttribute('gpu_index', new BufferAttribute(indices, 1));

  return pickerGeometry;
};

export const copyGeometryValues = (pickerMesh, mesh) => {
  pickerMesh['originalObject'] = mesh;
  pickerMesh.position.copy(mesh.position);
  pickerMesh.rotation.copy(mesh.rotation);
  pickerMesh.scale.copy(mesh.scale);
};

/** Create basic gpu picker mesh from existing objects */
export const getBasicPicker = (object, constructor) => {
  // Clone geometry and add gpu_index
  const pickerGeometry = geometryWithIndex(object);

  // Create new picker material from existing material(s)
  let pickerMaterial;
  if (Array.isArray(object.material)) {
    pickerMaterial = object.material.map(material => {
      return new BasicPickerMaterial(material.side);
    });
  } else {
    pickerMaterial = new BasicPickerMaterial(object.material.side);
  }

  const pickerMesh = new constructor(pickerGeometry, pickerMaterial);
  copyGeometryValues(pickerMesh, object);

  return pickerMesh;
};

/** Create basic gpu picker mesh from sprite types */
export const getCustomSpritePicker = (mesh) => {
  const material = mesh.material;
  const constructor = mesh.constructor;

  // Clone geometry and add gpu_index
  const pickerGeometry = geometryWithIndex(mesh);

  // Clone material with gpuPicker=true
  const pickerMaterial = new CustomSpriteMaterial({
    map: material.map,
    gpuPicker: true,
    size: material.size
  });

  const pickerMesh = new constructor(pickerGeometry, pickerMaterial);
  copyGeometryValues(pickerMesh, mesh);

  return pickerMesh;
};

export default class GPUPicker {
  private name
  public debug = false;
  public scene : Scene
  private viewer: Viewer
  private camera: Camera
  private numSections = 3;
  private sections;
  public target : WebGLRenderTarget
  private _needsUpdate = false;
  public result;

  constructor(viewer: Viewer, name) {
    this.name = name;
    this.viewer = viewer;
    this.scene = new Scene();

    this.target = new WebGLRenderTarget(0, 0, {
      minFilter: NearestFilter,
      magFilter: NearestFilter,
      format: RGBAFormat,
      encoding: LinearEncoding,
      generateMipmaps: false
    });

    this.clearSections();
  }

  get height() {
    return this.target.height;
  }

  get width() {
    return this.target.width;
  }

  get renderer(): WebGLRenderer {
    return this.viewer.renderer;
  }

  get needsUpdate() {
    return this._needsUpdate;
  }

  set needsUpdate(value) {
    if (this.debug) {
      console.log(`Update ${this.name} picker state`, value);
    }

    this._needsUpdate = value;
  }

  remove(mesh) {
    this.scene.remove(mesh);
    this.needsUpdate = true;
  }

  add(mesh) {
    this.scene.add(mesh);
    mesh.material.transparent = false;
    this.needsUpdate = true;
  }

  clear() {
    this.scene.clear();
  }

  value(event) {
    let uv = eventToPixel(event);
    let tX = uv.x;
    let tY = this.height - uv.y;

    let sectionWidth = Math.floor(this.width/this.numSections);
    let sectionHeight = Math.floor(this.height/this.numSections);
    let sectionX = Math.floor(tX/sectionWidth);
    let sectionY = Math.floor(tY/sectionHeight);
    let sectionIndex = sectionX + (sectionY * this.numSections);

    tX = tX % sectionWidth;
    tY = tY % sectionHeight;
    let pixelIndex = 4 * (tX + tY * sectionWidth);

    if (this.sections[sectionIndex] === null) {
      const timeStart = performance.now();

      let startX = sectionX*sectionWidth;
      let startY = sectionY*sectionHeight;

      let pixelBuffer = new Uint8Array(4 * sectionWidth * sectionHeight);
      this.renderer.readRenderTargetPixels(this.target, startX, startY,
        sectionWidth, sectionHeight, pixelBuffer);

      // Update section results
      this.sections[sectionIndex] = pixelBuffer;

      const timeEnd = performance.now();
      let time = (timeEnd - timeStart).toFixed(1);

      if (this.debug) {
        console.log(`Render ${this.name} picker: ${time}ms`);
      }
    }

    let result = this.sections[sectionIndex];
    if (!result) return null;

    let mouseResult = new Uint8Array([
      result[pixelIndex + 0],
      result[pixelIndex + 1],
      result[pixelIndex + 2],
      result[pixelIndex + 3]
    ]);

    let pickerID = (mouseResult[0] << 24)
      + (mouseResult[1] << 16)
      + (mouseResult[2] << 8)
      + mouseResult[3];

    return (pickerID >= 0) ? pickerID : null;
  }

  update() {
    if (!this.needsUpdate) return;

    this.clearSections();
    this.updateBaseIDs();

    this.needsUpdate = false;

    const oldClearColor = new Color();
    const newClearColor = new Color(0xffffff);

    let oldRenderTarget = this.renderer.getRenderTarget();
    this.renderer.getClearColor(oldClearColor);
    let oldClearAlpha = this.renderer.getClearAlpha();

    this.renderer.setRenderTarget(this.target);
    this.renderer.setClearColor(newClearColor);
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
    this.renderer.setRenderTarget(oldRenderTarget);
    this.renderer.setClearColor(oldClearColor);
    this.renderer.setClearAlpha(oldClearAlpha);
  }

  clearSections() {
    this.sections = new Array(this.numSections**2).fill(null);
  }

  updateBaseIDs() {
    let currentBaseID = 0;
    this.scene.children.forEach((child: Mesh) => {
      let geometry = child.geometry as BufferGeometry;
      let indices = geometry.attributes.gpu_index.array as any;

      for (var i=0;i<indices.length;i++) {
        indices[i] = i + currentBaseID;
      }

      currentBaseID += indices.length;
      geometry.attributes.gpu_index.needsUpdate = true;
    });
  }

  setCamera(camera) {
    this.camera = camera;
  }

  getClosestCoordinate(gpuIndex, event) {
    let object = this.getSelectedObject(gpuIndex);
    const coordinate = this.getObjectPosition(object, gpuIndex, event);

    if (object && object['originalObject']) {
      object = object['originalObject'];
    }

    return {object, coordinate};
  }

  getSelectedObject(gpuIndex): Object3D {
    let object = null;
    if (gpuIndex === null) {
      return object;
    }

    this.scene.children.forEach((child: Object3D) => {
      if (object !== null) return;

      let geometry = (child as any).geometry as BufferGeometry;
      let indices = geometry.attributes.gpu_index.array as Array<number>;

      // Determine if the index exists inside this object
      let minIndex = indices[0];
      let maxIndex = indices[indices.length - 1];
      if ((gpuIndex < minIndex) || (gpuIndex > maxIndex)) {
        return;
      }

      let arrayIndex = indices.indexOf(gpuIndex);
      if (arrayIndex === -1) return;

      object = child;
    });

    return object;
  }

  getObjectPosition(object, gpuIndex, event): Vector3 {
    let coordinate = null;
    if (!object) return coordinate;

    const geometry = object.geometry as BufferGeometry;
    const indices = geometry.attributes.gpu_index.array as Array<number>;
    const positions = geometry.attributes.position;
    const index = indices.indexOf(gpuIndex);

    const parameters = {geometry, indices, positions, index};

    if (object instanceof Points) {
      coordinate = this.coordinateFromBuffer(object, positions, index);
    } else if (object instanceof Mesh) {
      coordinate = this.coordinateFromMesh(object, parameters, event);
    } else if (object instanceof LineSegments) {
      coordinate = this.coordinateFromLine(object, parameters, event);
    }

    return coordinate;
  }

  /** @private */
  coordinateFromMesh(object, parameters, event) {
    const {geometry, positions, index} = parameters;

    let coordinate = null;
    let trianglesToCheck = [];

    if (geometry.index !== null) {
      const faces = geometry.index.array as Array<number>;
      const faceIndices = allIndexOf(faces, index);

      // Multiple base index might exist for this position. All
      // of them need to be added and checked for validity
      faceIndices.forEach(faceIndex => {
        faceIndex = Math.floor(faceIndex/3) * 3;
        trianglesToCheck.push([
          faces[faceIndex],
          faces[faceIndex + 1],
          faces[faceIndex + 2]
        ]);
      });
    } else {
      // Only one mesh face will exist
      const faceIndex = Math.floor(index/3) * 3;
      trianglesToCheck.push([faceIndex, faceIndex + 1, faceIndex + 2]);
    }

    const raycaster = new RayCaster(this.viewer, event);

    trianglesToCheck.forEach(face => {
      const P1 = this.coordinateFromBuffer(object, positions, face[0]);
      const P2 = this.coordinateFromBuffer(object, positions, face[1]);
      const P3 = this.coordinateFromBuffer(object, positions, face[2]);
      const result = raycaster.intersectTriangle(P1, P2, P3);
      if (!result) return;

      coordinate = result;
    });

    return coordinate;
  }

  /** @private */
  coordinateFromLine(object, parameters, event) {
    const {geometry, positions, index} = parameters;

    let isFirstPoint;
    if (geometry.index !== null) {
      // Index positions (check position in index array);
      const pointIndices = geometry.index.array as Array<number>;
      const pointIndex = pointIndices.indexOf(index);
      isFirstPoint = (pointIndex % 2) === 0;
    } else {
      // Non-index positions
      isFirstPoint = (index % 2) === 0;
    }

    const P1 = this.coordinateFromBuffer(object, positions, index);
    const P2 = isFirstPoint
      ? this.coordinateFromBuffer(object, positions, index + 1)
      : this.coordinateFromBuffer(object, positions, index - 1);

    const lineMaterial = new LineBasicMaterial({});
    const lineGeometry = new BufferGeometry().setFromPoints([P1, P2]);
    const line = new Line( lineGeometry, lineMaterial );
    const raycaster = new RayCaster(this.viewer, event);
    const results = raycaster.intersectObject(line);

    return (results.length > 0)
      ? results[0].point
      : null;
  }

  /** @private */
  coordinateFromBuffer(object, positions, index) {
    return new Vector3()
      .fromBufferAttribute(positions, index)
      .applyMatrix4(object.matrixWorld);
  }
}
